import * as React from 'react';
import { Decimal } from '@insio/decimal';
import { VatMode, type CurrencyCode } from '@insio/shared-types';
import { VatRateSelector, type VatRateOption } from '../../../components/inputs';
import { Icon } from '../../../components/icons/Icon';
import { ErrorSummary } from '../../../components/errors';
import { ExpectedAmountHint } from '../../../components/ExpectedAmountHint';
import { Badge } from '../../../components/Badge';
import { Select } from '../../../components/selects/Select';
import { InvoiceLineField, NumericLineField, TextLineField } from './InvoiceLineField';
import { useFormatAmount } from '../../../hooks/decimal-hooks';
import { useTranslation } from '../../../providers/UIProvider';
import {
  useInvoiceCurrencyCode,
  useInvoiceIsVatDocument
} from '../../../providers/InvoiceProvider';
import { cn, getIssueExpected } from '../../../utils';
import type {
  InvoiceLine as InvoiceLineType,
  InvoiceLineAmounts
} from '../../../graphql/generated/graphql';
import { computeMismatchFields, isLineLevelIssue, type ComputableField } from './utils';
import { numericDisplayValue, type LineNumericBuffer, type ValidationIssueLookup } from './hooks';
import { Button } from '../../../components/Button';
import type { InvoiceLineExtraField } from './invoice-line-extra-field';

/**
 * Three responsive layouts, driven by container queries on a parent named
 * `lines` (set by `InvoiceLinesForm`):
 *   - narrow  (<36rem): single-column stack
 *
 *   - middle (≥36rem, <48rem): 12-column grid, totals on their own row
 *       row 1: name (3) | qty (1) | unit (1) | unit price (2) | discount (2) | recycling (1)
 *       row 2: code (3) | ean (2) | vat rate (3, col 6) | vat amount (3, col 9)
 *       row 3:                     total no VAT (3, col 6) | total incl VAT (3, col 9)
 *
 *   - wide   (≥48rem): 12-column compact grid
 *       row 1: name (3) | qty (1) | unit (1) | unit price (2) | discount (2) | recycling (1)
 *       row 2: code (3) | ean (2) | vat rate (2, col 7) | vat amount (2, col 9) | total no VAT (2, col 11)
 *                                                                                   total incl VAT (2, col 11)
 *
 *  All layouts render as a card with a header containing the line number,
 *  the VAT-mode badge and recompute/delete buttons. Host fields (`extraFields`)
 *  take a row of their own under the amounts.
 */
const INVOICE_LINE_GRID_COLS = 'grid-cols-1 @2xl/lines:grid-cols-12';

const SPAN_NAME = '@2xl/lines:col-span-3';
const SPAN_CODE = '@2xl/lines:col-span-3 @2xl/lines:row-start-2';
const SPAN_EAN = '@2xl/lines:col-span-2 @2xl/lines:row-start-2';
const SPAN_NAME_NO_VAT = '@2xl/lines:col-span-3';
const SPAN_QTY = '@2xl/lines:col-span-1';
const SPAN_UNIT = '@2xl/lines:col-span-1';
const SPAN_UNIT_PRICE = '@2xl/lines:col-span-2';
const SPAN_DISCOUNT = '@2xl/lines:col-span-2';
const SPAN_RECYCLING = '@2xl/lines:col-span-1';
// Middle: vatRate/vatAmount start at col 6, span 3 each → fills cols 6–11. Wide: restore col 7/9, span 2.
const SPAN_VAT_RATE =
  '@2xl/lines:col-span-3 @2xl/lines:row-start-2 @2xl/lines:col-start-6 @3xl/lines:col-span-2 @3xl/lines:col-start-7';
const SPAN_VAT_AMOUNT =
  '@2xl/lines:col-span-3 @2xl/lines:row-start-2 @2xl/lines:col-start-9 @3xl/lines:col-span-2 @3xl/lines:col-start-9';
// Middle: totals on row 3, same cols as vat fields above → perfect vertical alignment.
// Wide: reset row-start to auto, col-span back to 2, col-start to 11 — identical to original.
const SPAN_TOTAL_NO_VAT =
  '@2xl/lines:col-span-3 @2xl/lines:row-start-3 @2xl/lines:col-start-6 @3xl/lines:col-span-2 @3xl/lines:row-start-auto @3xl/lines:col-start-11';
const SPAN_TOTAL_INCL_VAT =
  '@2xl/lines:col-span-3 @2xl/lines:row-start-3 @2xl/lines:col-start-9 @3xl/lines:col-span-2 @3xl/lines:row-start-auto @3xl/lines:col-start-11';
// Host fields sit in a row of their own under the amounts.
const SPAN_EXTRA = '@2xl/lines:col-span-6 @3xl/lines:col-span-5';

// Shared empty mismatch set — referenced while a line has no computed amounts
// yet, so we don't allocate a fresh `Set` per render.
const EMPTY_MISMATCH: ReadonlySet<ComputableField> = new Set();

/** Selectable line VAT modes — legacy EXEMPT_* subtypes are display-only. */
const SELECTABLE_VAT_MODES: VatMode[] = [
  VatMode.STANDARD,
  VatMode.REVERSE_CHARGE,
  VatMode.EXEMPT_OTHER
];

export interface InvoiceLineProps {
  line: Partial<InvoiceLineType>;
  index: number;
  vatRateOptions?: readonly VatRateOption[];
  disabled?: boolean;
  /** Form-wide flag: whether unit prices are entered VAT-inclusive. Drives the unit-price label. */
  isUnitPriceInclVat: boolean;
  /** Memoized validation lookup shared by all lines. */
  issues: ValidationIssueLookup;
  /** Buffered numeric input text for this line; `undefined` while untouched. */
  numericBuffer?: LineNumericBuffer;
  onUpdateField: (
    index: number,
    field: keyof InvoiceLineType,
    value: string | Decimal | null
  ) => void;
  onNumericChange: (index: number, field: keyof InvoiceLineType, raw: string) => void;
  onNumericBlur: (index: number, field: keyof InvoiceLineType) => void;
  onDelete: (index: number) => void;
  /** Latest backend-computed amounts for this line, or `null` while pending /
   * never run. Drives the "expected value" hints under the total inputs. */
  computed?: InvoiceLineAmounts | null;
  /** Whether `computed` was computed for other inputs than the line shows (typed text
   * included); enables the recompute button, which then waits for a fresh result. */
  isStale?: boolean;
  /** When provided, an "apply" arrow appears next to each hinted amount;
   * clicking it pushes the computed value back into the input. */
  onApplyComputed?: (index: number, field: ComputableField) => void;
  /** When provided, an "apply" arrow appears next to a field's validation issue
   * whenever it carries `meta.expected`; clicking it fills that expected value. */
  onApplyExpected?: (index: number, field: keyof InvoiceLineType, expected: string) => void;
  /** Fills every blank derivable field on the line in one click. */
  onRecompute?: (index: number) => void;
  /** Fires when focus enters any field on this line; clears stale issues. */
  onFocusLine?: (index: number) => void;
  /** Host fields drawn under the amounts; need `onPatchLine` to save. */
  extraFields?: readonly InvoiceLineExtraField[];
  /** Merges a host field's change into the line. */
  onPatchLine?: (index: number, patch: Partial<InvoiceLineType>) => void;
}

/** Memoized — all callback props are index-based and referentially stable in
 * `InvoiceLinesForm`, so typing in one line doesn't re-render the others. */
export const InvoiceLine: React.FC<InvoiceLineProps> = React.memo(function InvoiceLine({
  line,
  index,
  vatRateOptions,
  disabled = false,
  isUnitPriceInclVat,
  issues,
  numericBuffer,
  onUpdateField,
  onNumericChange,
  onNumericBlur,
  onDelete,
  computed,
  isStale = false,
  onApplyComputed,
  onApplyExpected,
  onRecompute,
  onFocusLine,
  extraFields,
  onPatchLine
}) {
  const t = useTranslation('modules.modals.editInvoiceLines');
  const tVatMode = useTranslation('components.vatMode');
  const currencyCode = (useInvoiceCurrencyCode() ?? undefined) as CurrencyCode | undefined;
  const formatAmount = useFormatAmount(currencyCode);
  const lineIssues = issues.getLineIssues(index);
  const severity = issues.getLineSeverity(index);
  const lineLevelIssues = lineIssues.filter(isLineLevelIssue);
  const lineNumber = line.lineNumber ?? index + 1;

  const isVatDocument = useInvoiceIsVatDocument();

  // Line VAT mode — a compact badge in the card header; clicking it swaps in a
  // small select (3 active modes; a stored legacy value stays listed).
  const [isEditingVatMode, setIsEditingVatMode] = React.useState(false);
  const lineVatMode = line.vatMode ?? '';
  const isNonStandardVatMode = lineVatMode !== '' && lineVatMode !== VatMode.STANDARD;
  const vatModeOptions = React.useMemo(() => {
    const modes =
      lineVatMode && !SELECTABLE_VAT_MODES.includes(lineVatMode)
        ? [...SELECTABLE_VAT_MODES, lineVatMode]
        : SELECTABLE_VAT_MODES;
    return [
      { value: '', label: '—' },
      ...modes.map(mode => ({ value: mode, label: tVatMode(mode, { defaultValue: mode }) }))
    ];
  }, [lineVatMode, tVatMode]);

  const fieldHasError = (field: string) => issues.fieldHasError(index, field);
  const fieldHasIssue = (field: string) => issues.fieldHasIssue(index, field);
  const fieldWorstIssue = (field: string) => issues.fieldWorstIssue(index, field);
  const numericValue = (field: keyof InvoiceLineType, stored: Decimal | null | undefined) =>
    numericDisplayValue(numericBuffer, field, stored, currencyCode);

  /** Fields whose typed value diverges from (or is blank vs.) `computed`. */
  const computedMismatchFields = React.useMemo(
    () => (computed ? computeMismatchFields(computed, line, isVatDocument) : EMPTY_MISMATCH),
    [computed, line, isVatDocument]
  );
  const canRecompute = computedMismatchFields.size > 0 || isStale;

  const containerClass = cn(
    'rounded-lg border p-sm',
    severity === 'error'
      ? 'border-error-border bg-error-bg'
      : severity === 'warning'
        ? 'border-warn-border bg-warn-bg'
        : 'border-border-base bg-white'
  );

  const hasMismatch = (field: ComputableField) => computedMismatchFields.has(field);

  const inputBorderClass = (field: string) =>
    cn(
      fieldHasError(field) && 'border-error-border',
      !fieldHasError(field) &&
        (fieldHasIssue(field) || hasMismatch(field as ComputableField)) &&
        'border-warn-border'
    );

  /** Apply-button props for a field whose validation issue carries an expected
   * value — spread into the field so the arrow fills it from `meta.expected`. */
  const applyExpectedProps = (field: keyof InvoiceLineType) => {
    if (!onApplyExpected || !fieldHasIssue(field)) return undefined;
    const expected = getIssueExpected(fieldWorstIssue(field) ?? {});
    if (expected == null) return undefined;
    return {
      onApplyExpected: () => onApplyExpected(index, field, expected),
      applyExpectedLabel: t('warnings.applyExpected')
    };
  };

  const expectedHint = (field: ComputableField) => {
    // A validation issue on the field carries its own apply affordance — don't
    // also show the computed hint for the same cell.
    if (fieldHasIssue(field)) return undefined;
    const amount = computed?.[field];
    return (
      <ExpectedAmountHint
        show={hasMismatch(field)}
        label={t('hintExpected')}
        amount={amount != null ? formatAmount(amount) : ''}
        onApply={onApplyComputed ? () => onApplyComputed(index, field) : undefined}
        applyLabel={t('warnings.applyExpected')}
      />
    );
  };

  const totalAmountKey = isVatDocument ? 'totalAmountNoVat' : 'totalAmount';
  return (
    // `onFocus` bubbles (React focusin): clears the line's stale issues as soon
    // as the user enters any field, not while typing.
    <div className={containerClass} onFocus={() => onFocusLine?.(index)}>
      {/* Line header: line number + VAT-mode badge + recompute/delete buttons. */}
      <div className='flex justify-between mb-sm gap-lg items-start'>
        <div className='flex items-center gap-sm min-w-0'>
          <span className='text-xs font-semibold text-foreground-muted uppercase tabular-nums'>
            #{lineNumber}
          </span>

          {isVatDocument &&
            (isEditingVatMode ? (
              <Select
                id={`vatMode-${index}`}
                options={vatModeOptions}
                value={lineVatMode}
                defaultOpen
                height='sm'
                disabled={disabled}
                onChange={next => {
                  const picked = Array.isArray(next) ? next[0] : next;
                  const code = picked == null ? '' : String(picked);
                  onUpdateField(index, 'vatMode', code === '' ? null : code);
                  setIsEditingVatMode(false);
                }}
                onBlur={() => setIsEditingVatMode(false)}
                className='w-48'
              />
            ) : (
              <button
                type='button'
                onClick={() => setIsEditingVatMode(true)}
                disabled={disabled}
                aria-label={t('colVatMode')}
                title={t('colVatMode')}
                className='disabled:pointer-events-none'
              >
                <Badge
                  size='sm'
                  color={isNonStandardVatMode ? 'info' : 'neutral'}
                  className={cn(
                    'cursor-pointer',
                    !isNonStandardVatMode && 'opacity-60 hover:opacity-100 transition-opacity'
                  )}
                >
                  {isNonStandardVatMode
                    ? tVatMode(lineVatMode, { defaultValue: lineVatMode })
                    : t('colVatMode')}
                </Badge>
              </button>
            ))}
        </div>

        <div className='flex items-center gap-2xs'>
          {onRecompute && (
            <Button
              variant='ghost'
              onClick={() => onRecompute(index)}
              disabled={disabled || !canRecompute}
              aria-label={t('recomputeLine')}
              title={t('recomputeLine')}
              className='p-xs rounded text-foreground-muted hover:text-primary-text hover:bg-primary-bg transition-colors disabled:opacity-40 disabled:pointer-events-none'
            >
              <Icon name='arrows-rotate' className='w-4 h-4' />
            </Button>
          )}

          <Button
            variant='ghost'
            onClick={() => onDelete(index)}
            disabled={disabled}
            aria-label={t('deleteLine')}
            className='p-xs rounded text-foreground-muted hover:text-error-text hover:bg-error-bg transition-colors disabled:opacity-40 disabled:pointer-events-none'
          >
            <Icon name='trash-can' className='w-4 h-4' />
          </Button>
        </div>
      </div>

      <div className={cn('grid gap-x-sm gap-y-xs @2xl/lines:gap-xs', INVOICE_LINE_GRID_COLS)}>
        {/* Description */}
        <TextLineField
          className={isVatDocument ? SPAN_NAME : SPAN_NAME_NO_VAT}
          id={`description-${index}`}
          label={t('colName')}
          isError={fieldHasError('description')}
          issue={fieldHasIssue('description') ? fieldWorstIssue('description') : undefined}
          value={line.description ?? ''}
          onChange={v => onUpdateField(index, 'description', v)}
          disabled={disabled}
          placeholder={t('noName')}
          inputClassName={cn('min-w-24', inputBorderClass('description'))}
        />
        {/* Quantity */}
        <NumericLineField
          id={`quantity-${index}`}
          label={t('colQuantity')}
          className={SPAN_QTY}
          isError={fieldHasError('quantity')}
          issue={fieldHasIssue('quantity') ? fieldWorstIssue('quantity') : undefined}
          value={numericValue('quantity', line.quantity)}
          onChange={v => onNumericChange(index, 'quantity', v)}
          onBlur={() => onNumericBlur(index, 'quantity')}
          disabled={disabled}
          inputClassName={inputBorderClass('quantity')}
        />

        {/* Unit */}
        <TextLineField
          id={`unit-${index}`}
          label={t('colUnit')}
          align='right'
          className={SPAN_UNIT}
          isError={fieldHasError('unit')}
          issue={fieldHasIssue('unit') ? fieldWorstIssue('unit') : undefined}
          value={line.unit ?? ''}
          onChange={v => onUpdateField(index, 'unit', v)}
          disabled={disabled}
          inputClassName={cn(inputBorderClass('unit'))}
        />

        {/* Unit Price */}
        <NumericLineField
          id={`unitPrice-${index}`}
          label={
            isVatDocument
              ? `${t('colPricePerUnit')} (${t(isUnitPriceInclVat ? 'unitPriceInclVatSuffix' : 'unitPriceExclVatSuffix')})`
              : t('colPricePerUnit')
          }
          className={SPAN_UNIT_PRICE}
          isError={fieldHasError('unitPrice')}
          issue={fieldHasIssue('unitPrice') ? fieldWorstIssue('unitPrice') : undefined}
          hint={expectedHint('unitPrice')}
          value={numericValue('unitPrice', line.unitPrice)}
          onChange={v => onNumericChange(index, 'unitPrice', v)}
          onBlur={() => onNumericBlur(index, 'unitPrice')}
          disabled={disabled}
          inputClassName={inputBorderClass('unitPrice')}
          currencyCode={currencyCode}
        />

        {/* Discount */}
        <NumericLineField
          id={`discountPercent-${index}`}
          label={t('colDiscount')}
          className={SPAN_DISCOUNT}
          isError={fieldHasError('discountPercent')}
          issue={fieldHasIssue('discountPercent') ? fieldWorstIssue('discountPercent') : undefined}
          value={numericValue('discountPercent', line.discountPercent)}
          onChange={v => onNumericChange(index, 'discountPercent', v)}
          onBlur={() => onNumericBlur(index, 'discountPercent')}
          disabled={disabled}
          inputClassName={inputBorderClass('discountPercent')}
        />

        {/* Recycling Fee */}
        <NumericLineField
          id={`recyclingFee-${index}`}
          label={t('colRecyclingFee')}
          className={SPAN_RECYCLING}
          isError={fieldHasError('recyclingFee')}
          issue={fieldHasIssue('recyclingFee') ? fieldWorstIssue('recyclingFee') : undefined}
          value={numericValue('recyclingFee', line.recyclingFee)}
          onChange={v => onNumericChange(index, 'recyclingFee', v)}
          onBlur={() => onNumericBlur(index, 'recyclingFee')}
          disabled={disabled}
          inputClassName={cn('text-foreground-muted', inputBorderClass('recyclingFee'))}
          currencyCode={currencyCode}
        />

        <TextLineField
          id={`code-${index}`}
          label={t('colCode')}
          align='right'
          className={SPAN_CODE}
          isError={fieldHasError('code')}
          issue={fieldHasIssue('code') ? fieldWorstIssue('code') : undefined}
          value={line.code ?? ''}
          onChange={v => onUpdateField(index, 'code', v)}
          disabled={disabled}
          inputClassName={cn(inputBorderClass('code'))}
        />

        <TextLineField
          id={`ean-${index}`}
          label={t('colEan')}
          align='right'
          className={SPAN_EAN}
          isError={fieldHasError('ean')}
          issue={fieldHasIssue('ean') ? fieldWorstIssue('ean') : undefined}
          value={line.ean ?? ''}
          onChange={v => onUpdateField(index, 'ean', v)}
          disabled={disabled}
          inputClassName={cn(inputBorderClass('ean'))}
        />

        {/* VAT Rate — uses a Selector (not a plain Input), so it stays on the
            raw `InvoiceLineField` rather than one of the input wrappers. */}
        <InvoiceLineField
          visible={isVatDocument}
          label={t('colVatRate')}
          htmlFor={`vatRate-${index}`}
          align='right'
          className={SPAN_VAT_RATE}
          isError={fieldHasError('vatRate')}
          issue={fieldHasIssue('vatRate') ? fieldWorstIssue('vatRate') : undefined}
        >
          <VatRateSelector
            id={`vatRate-${index}`}
            vatRateOptions={vatRateOptions}
            value={line.vatRate?.toNumber() ?? null}
            onChange={rate =>
              onUpdateField(index, 'vatRate', rate == null ? null : new Decimal(rate))
            }
            disabled={disabled}
            height='sm'
            className='w-full min-w-16 text-sm @2xl/lines:text-right'
          />
        </InvoiceLineField>

        {/* VAT Amount */}
        <NumericLineField
          id={`vatAmount-${index}`}
          visible={isVatDocument}
          label={t('colVatTotal')}
          className={SPAN_VAT_AMOUNT}
          isError={fieldHasError('vatAmount')}
          issue={fieldHasIssue('vatAmount') ? fieldWorstIssue('vatAmount') : undefined}
          {...applyExpectedProps('vatAmount')}
          hint={expectedHint('vatAmount')}
          value={numericValue('vatAmount', line.vatAmount)}
          onChange={v => onNumericChange(index, 'vatAmount', v)}
          onBlur={() => onNumericBlur(index, 'vatAmount')}
          disabled={disabled}
          inputClassName={cn('text-foreground-muted', inputBorderClass('vatAmount'))}
          currencyCode={currencyCode}
        />

        {/* Total excl. VAT */}
        <NumericLineField
          id={`totalAmountNoVat-${index}`}
          label={t(totalAmountKey)}
          className={cn(SPAN_TOTAL_NO_VAT)}
          isError={fieldHasError('totalAmountNoVat')}
          issue={
            fieldHasIssue('totalAmountNoVat') ? fieldWorstIssue('totalAmountNoVat') : undefined
          }
          {...applyExpectedProps('totalAmountNoVat')}
          hint={expectedHint('totalAmountNoVat')}
          value={numericValue('totalAmountNoVat', line.totalAmountNoVat)}
          onChange={v => onNumericChange(index, 'totalAmountNoVat', v)}
          onBlur={() => onNumericBlur(index, 'totalAmountNoVat')}
          disabled={disabled}
          inputClassName={cn('text-foreground-muted', inputBorderClass('totalAmountNoVat'))}
          currencyCode={currencyCode}
        />

        {/* Total incl. VAT */}
        <NumericLineField
          visible={isVatDocument}
          id={`totalAmountInclVat-${index}`}
          label={t('colTotal')}
          className={SPAN_TOTAL_INCL_VAT}
          isError={fieldHasError('totalAmountInclVat')}
          issue={
            fieldHasIssue('totalAmountInclVat') ? fieldWorstIssue('totalAmountInclVat') : undefined
          }
          {...applyExpectedProps('totalAmountInclVat')}
          hint={expectedHint('totalAmountInclVat')}
          value={numericValue('totalAmountInclVat', line.totalAmountInclVat)}
          onChange={v => onNumericChange(index, 'totalAmountInclVat', v)}
          onBlur={() => onNumericBlur(index, 'totalAmountInclVat')}
          disabled={disabled}
          inputClassName={cn('font-semibold', inputBorderClass('totalAmountInclVat'))}
          currencyCode={currencyCode}
        />
      </div>

      {extraFields && extraFields.length > 0 && onPatchLine && (
        <div
          className={cn('grid gap-x-sm gap-y-xs mt-xs @2xl/lines:gap-xs', INVOICE_LINE_GRID_COLS)}
        >
          {extraFields.map(field => {
            const inputId = `${field.key}-${index}`;
            return (
              <InvoiceLineField
                key={field.key}
                label={field.label}
                htmlFor={inputId}
                className={SPAN_EXTRA}
              >
                {field.render({
                  line,
                  index,
                  inputId,
                  disabled,
                  onChange: patch => onPatchLine(index, patch)
                })}
              </InvoiceLineField>
            );
          })}
        </div>
      )}

      {lineLevelIssues.length > 0 && (
        <ErrorSummary issues={lineLevelIssues} className='mt-sm pt-xs border-t border-error-bg' />
      )}
    </div>
  );
});

InvoiceLine.displayName = 'InvoiceLine';
